/* eslint-env browser */
// The portal's list table, as the approved W2_Portal_Tables board draws it
// (and BookingHistory / Drivers already do): one white card with a header
// row (the count, a search box, Rows: Comfortable / Compact, Columns,
// Export), a sticky header, zebra rows, numbers and money right-aligned in
// tabular figures, status as an icon and a word, row actions as icon
// buttons plus a "More actions" menu, an empty state that says why, grey
// skeleton rows on the first load, and paging. It replaces material-table:
// the column list keeps material-table's names (title, field, render, type,
// lookup, hidden, defaultSort, customSort, exportValue...).
//
// It only shows the rows it is given: no reads, no writes, no listeners.
// Search, sort and paging run in memory; a page with server search passes
// serverSearch and its own searchText / onSearchChange.
//
// Minimal use:
//   <DataTable title={t('cancellation_reasons_title')} rows={rows}
//     columns={[{ title: t('reason'), field: 'label' }]} />
//
// See work/portal3/MIGRATION.md for every prop with examples.
//
// stateKey="drivers": the search box and the page are kept for this tab
// (pageState.js) while staff open a row and come back, as the page's own
// filters are with usePageState (owner, 28 Oct: never reset a list's
// filters on the way back from a detail page).
import React, { useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';
import Collapse from '@mui/material/Collapse';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import DensitySmallRoundedIcon from '@mui/icons-material/DensitySmallRounded';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { tokens as c } from '../../theme';
import { EmptyState, InlineMessage, TableSkeletonRows } from '../feedback';
import { exportCsv, printTable, datedFileName, formatAddisDate, formatAddisTime, formatAddisDateTime } from '../../common/exportUtils';
import SearchField from './SearchField';
import StatusChip, { YesNo } from './StatusChip';
import {
  EMPTY_TEXT,
  calmCaps,
  normalizeColumns,
  getByPath,
  lookupText,
  formatNumber,
  formatMoney,
  sortRows,
  searchRows,
  exportColumnsOf,
  readTablePref,
  writeTablePref,
} from './tableUtils';
import { usePageState } from './pageState';
import { pressPoint, selectingTextIn } from '../../common/textSelection';

// The board's header and zebra tint (BookingHistory uses the same value).
const ZEBRA = '#FAFAFB';
const DEFAULT_PAGE_SIZES = [25, 50, 100];
const ACTION_SLOT = 40;

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

// A click on one of these inside a row is its own action, never "open the row".
const INTERACTIVE =
  'a,button,input,select,textarea,label,[role="button"],[role="switch"],[role="checkbox"],[role="combobox"],[role="option"],[role="menuitem"],[role="link"],[data-no-row-click]';

const isShown = (action, row) => {
  if (!action) return false;
  if (typeof action.show === 'function') return !!action.show(row);
  return action.show !== false;
};

const isDisabled = (action, row) => (typeof action.disabled === 'function' ? !!action.disabled(row) : !!action.disabled);

export default function DataTable(props) {
  const {
    title,
    rows,
    columns,
    rowKey = 'id',
    loading = false,
    refreshing = false,
    error = null,
    countText,
    subtitle,
    headerContent,
    toolbar,
    search = true,
    searchPlaceholder,
    searchText,
    onSearchChange,
    serverSearch = false,
    searchFn,
    onRowClick,
    rowLabel,
    actions,
    detailPanel,
    selectable = false,
    selected,
    onSelectionChange,
    bulkActions,
    paging = true,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    defaultPageSize,
    page: pageProp,
    onPageChange,
    initialPage = 0,
    defaultSort,
    sort: sortProp,
    onSortChange,
    resortKey,
    prefsKey,
    stateKey,
    densityToggle = true,
    columnsMenu = true,
    exportCsv: canCsv = false,
    exportPrint: canPrint = false,
    exportFileName,
    exportTitle,
    exportSubtitle,
    exportLandscape = true,
    exportRows = 'visible',
    exportColumns,
    emptyState,
    minWidth = 720,
    maxHeight = 'calc(100vh - 220px)',
    ariaLabel,
    rowSx,
    sx,
  } = props;

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const uid = useSelector((state) => (state.auth && state.auth.profile ? state.auth.profile.uid : null));
  const settings = useSelector((state) => (state.settingsdata ? state.settingsdata.settings : null));

  const allCols = normalizeColumns(columns);
  const roleCols = allCols.filter((col) => !col.hidden);
  const hideableCols = roleCols.filter((col) => col.hideable);
  const colSig = allCols.map((col) => `${col.id}:${col.type}:${col.field || ''}:${col.hidden ? 1 : 0}`).join('|');
  const colsRef = useRef(allCols);
  colsRef.current = allCols;
  const searchFnRef = useRef(searchFn);
  const rowPressRef = useRef(null); // where the last row press began (a drag selects text)
  searchFnRef.current = searchFn;

  // ── Per-person view choices (this browser only) ───────────────────────
  const sizes = Array.isArray(pageSizeOptions) && pageSizeOptions.length ? pageSizeOptions : DEFAULT_PAGE_SIZES;
  const fallbackSize = sizes.includes(defaultPageSize) ? defaultPageSize : sizes[0];
  const [density, setDensity] = useState(() => (readTablePref(prefsKey, 'density', uid, 'comfortable') === 'compact' ? 'compact' : 'comfortable'));
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = Number(readTablePref(prefsKey, 'rpp', uid, fallbackSize));
    return sizes.includes(saved) ? saved : fallbackSize;
  });
  const [hiddenIds, setHiddenIds] = useState(() => {
    const saved = readTablePref(prefsKey, 'hidden', uid, null);
    return Array.isArray(saved) ? saved : allCols.filter((col) => col.defaultHidden).map((col) => col.id);
  });

  // ── Sort ─────────────────────────────────────────────────────────────
  const [sortState, setSortState] = useState(() => {
    if (defaultSort && defaultSort.by) return { by: defaultSort.by, dir: defaultSort.dir === 'desc' ? 'desc' : 'asc' };
    const col = allCols.find((cl) => cl.defaultSort === 'asc' || cl.defaultSort === 'desc');
    return col ? { by: col.id, dir: col.defaultSort } : { by: null, dir: 'asc' };
  });
  const sort = sortProp || sortState;
  const setSort = (next) => {
    if (onSortChange) onSortChange(next);
    if (!sortProp) setSortState(next);
  };

  // ── Search ───────────────────────────────────────────────────────────
  const [searchState, setSearchState] = usePageState(searchText === undefined ? stateKey : null, 'search', '');
  const query = searchText !== undefined ? String(searchText || '') : searchState;
  const setQuery = (value) => {
    if (onSearchChange) onSearchChange(value);
    if (searchText === undefined) setSearchState(value);
  };
  // Typing stays quick on 40,000 rows: the list follows a moment later.
  const deferredQuery = useDeferredValue(query);
  const trimmedQuery = query.trim();

  const list = Array.isArray(rows) ? rows : [];
  const filtered = useMemo(() => {
    const q = String(deferredQuery || '').trim();
    if (!q || serverSearch) return list;
    if (typeof searchFnRef.current === 'function') {
      const ql = q.toLowerCase();
      return list.filter((row) => searchFnRef.current(row, q, ql));
    }
    return searchRows(list, colsRef.current, q, settings);
    // colSig / resortKey stand in for the column functions (new each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, deferredQuery, serverSearch, colSig, resortKey, settings]);

  const sorted = useMemo(() => {
    if (!sort || !sort.by) return filtered;
    const col = colsRef.current.find((cl) => cl.id === sort.by);
    if (!col || (!col.sortable && typeof col.customSort !== 'function')) return filtered;
    return sortRows(filtered, col, sort.dir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort ? sort.by : null, sort ? sort.dir : null, colSig, resortKey]);

  // ── Paging (a page handed back by "Back" from a detail page is kept) ──
  const [pageState, setPageState] = usePageState(
    pageProp === undefined || pageProp === null ? stateKey : null,
    'page',
    Number.isInteger(initialPage) && initialPage > 0 ? initialPage : 0,
  );
  const page = pageProp !== undefined && pageProp !== null ? pageProp : pageState;
  const setPage = (next) => {
    if (onPageChange) onPageChange(next);
    if (pageProp === undefined || pageProp === null) setPageState(next);
  };
  const pageCount = paging ? Math.max(1, Math.ceil(sorted.length / rowsPerPage)) : 1;
  const safePage = Math.min(Math.max(0, Number(page) || 0), pageCount - 1);
  const pageRows = paging ? sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage) : sorted;

  // A new search or sort starts at the first page (only a real change).
  const listSig = JSON.stringify([trimmedQuery, sort ? sort.by : null, sort ? sort.dir : null]);
  const lastSig = useRef(listSig);
  useEffect(() => {
    if (lastSig.current === listSig) return;
    lastSig.current = listSig;
    if (safePage !== 0) setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSig]);

  // ── Keys, selection and open detail rows ─────────────────────────────
  const keyMap = useMemo(() => {
    const map = new Map();
    list.forEach((row, index) => {
      let key;
      if (typeof rowKey === 'function') key = rowKey(row, index);
      else if (row && row[rowKey] !== undefined && row[rowKey] !== null) key = row[rowKey];
      else key = `row-${index}`;
      map.set(row, String(key));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);
  const keyOf = (row) => keyMap.get(row) || '';

  const [selState, setSelState] = useState([]);
  const selectedKeys = Array.isArray(selected) ? selected.map(String) : selState;
  const setSelected = (keys) => {
    const unique = Array.from(new Set(keys.map(String)));
    if (onSelectionChange) onSelectionChange(unique, list.filter((row) => unique.includes(keyOf(row))));
    if (!Array.isArray(selected)) setSelState(unique);
  };
  const selectedRows = selectable ? list.filter((row) => selectedKeys.includes(keyOf(row))) : [];

  const [expanded, setExpanded] = useState([]);
  const toggleExpanded = (key) => setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev.concat([key])));

  // ── Menus ────────────────────────────────────────────────────────────
  const [rowMenu, setRowMenu] = useState(null); // { anchor, row }
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const yesText = L('yes', 'Yes');
  const noText = L('no', 'No');

  // ── Column visibility ────────────────────────────────────────────────
  const shownCols = roleCols.filter((col) => !col.hideable || !hiddenIds.includes(col.id));
  const hiddenCount = hideableCols.filter((col) => hiddenIds.includes(col.id)).length;
  const toggleColumn = (id) => {
    setHiddenIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat([id]);
      writeTablePref(prefsKey, 'hidden', uid, next);
      return next;
    });
  };

  const compact = density === 'compact';
  const rowHeight = compact ? 40 : 52;
  const changeDensity = (event, value) => {
    if (!value) return;
    setDensity(value);
    writeTablePref(prefsKey, 'density', uid, value);
  };

  // ── Row actions ──────────────────────────────────────────────────────
  const actionList = (Array.isArray(actions) ? actions : []).filter(Boolean);
  const inlineActions = actionList.filter((a) => a.inline === true);
  const menuActions = actionList.filter((a) => a.inline !== true);
  const hasActions = actionList.length > 0;
  const nameFor = (row) => {
    const name = typeof rowLabel === 'function' ? rowLabel(row) : '';
    return name ? `: ${name}` : '';
  };
  const actionsWidth = inlineActions.length * (ACTION_SLOT + 4) + (menuActions.length ? ACTION_SLOT + 4 : 0) + 24;

  // ── Texts ────────────────────────────────────────────────────────────
  const total = list.length;
  const info = { query: trimmedQuery, total, shown: sorted.length, loading };
  const defaultCount = loading
    ? L('loading', 'Loading…')
    : trimmedQuery && !serverSearch
      ? t('table_count_shown', { shown: fmt(sorted.length), total: fmt(total), defaultValue: '{{shown}} of {{total}}' })
      : t('table_count', { count: total, n: fmt(total), defaultValue_one: '{{n}} row', defaultValue_other: '{{n}} rows', defaultValue: '{{n}} rows' });
  const countLine = typeof countText === 'function' ? countText(info) : countText !== undefined && countText !== null ? countText : defaultCount;
  const tableName = ariaLabel || (typeof title === 'string' ? title : undefined);

  // ── Exports (Ethiopic-safe: UTF-8 CSV with a BOM, a printed table) ────
  const exportList = exportRows === 'all' ? list : sorted;
  const csvColumns = () => (Array.isArray(exportColumns) ? exportColumns : exportColumnsOf(allCols, { settings, yes: yesText, no: noText }));
  const printColumns = () =>
    Array.isArray(exportColumns)
      ? exportColumns.filter((col) => col.print !== false)
      : exportColumnsOf(allCols, { forPrint: true, settings, yes: yesText, no: noText });
  const baseName = exportFileName || 'export';
  const handleCsv = () => {
    setExportAnchor(null);
    exportCsv({ columns: csvColumns(), rows: exportList, fileName: datedFileName(baseName, 'csv') });
  };
  const handlePrint = () => {
    setExportAnchor(null);
    const sub =
      exportSubtitle !== undefined
        ? exportSubtitle
        : [typeof countLine === 'string' ? countLine : '', formatAddisDateTime(Date.now())].filter(Boolean).join(' · ');
    printTable({
      title: exportTitle || (typeof title === 'string' ? title : ''),
      subtitle: sub,
      columns: printColumns(),
      rows: exportList,
      fileName: datedFileName(baseName, ''),
      landscape: exportLandscape,
      lang: i18n.language,
    });
  };
  const exportDisabled = loading || exportList.length === 0;

  // ── Cells ────────────────────────────────────────────────────────────
  const subText = { fontSize: 12.5, color: c.TEXT_3, lineHeight: 1.45, whiteSpace: 'nowrap' };
  const emptyCell = <Box component="span" sx={{ color: c.TEXT_3 }}>{EMPTY_TEXT}</Box>;

  const renderCell = (col, row) => {
    if (typeof col.render === 'function') return col.render(row);
    if (col.type === 'status' && typeof col.status === 'function') {
      const st = col.status(row) || {};
      return <StatusChip tone={st.tone} icon={st.icon} label={st.label} />;
    }
    const raw = getByPath(row, col.field);
    if (col.type === 'boolean') return <YesNo value={raw} yes={yesText} no={noText} />;
    if (raw === null || raw === undefined || raw === '') return emptyCell;
    if (col.lookup) return String(lookupText(col, raw));
    switch (col.type) {
      case 'datetime':
        return (
          <Box sx={{ whiteSpace: 'nowrap' }}>
            <Box sx={{ fontWeight: 600 }}>{formatAddisDate(raw)}</Box>
            <Box sx={subText}>{formatAddisTime(raw)}</Box>
          </Box>
        );
      case 'date':
        return <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatAddisDate(raw)}</Box>;
      case 'time':
        return <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatAddisTime(raw)}</Box>;
      case 'number':
        return formatNumber(raw);
      case 'money':
        return formatMoney(raw, settings, col.withSymbol === true);
      default:
        return typeof raw === 'object' ? emptyCell : String(raw);
    }
  };

  const cellSxOf = (col) => ({
    ...(col.align === 'right' ? { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } : {}),
    ...(col.type === 'number' || col.type === 'money' ? { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } : {}),
    ...(col.nowrap ? { whiteSpace: 'nowrap' } : { overflowWrap: 'anywhere' }),
    ...(col.width ? { width: col.width } : {}),
    ...(col.minWidth ? { minWidth: col.minWidth } : {}),
    ...(col.cellSx || {}),
  });

  // ── Row click (never from a button, switch, link or menu in the row) ──
  const handleRowClick = (event, row) => {
    if (typeof onRowClick !== 'function') return;
    const rowEl = event.currentTarget;
    const target = event.target;
    // A click in a menu or dialog opened from the row reaches here through
    // the React tree but is not inside the row on the page.
    if (!target || !rowEl.contains(target)) return;
    const hit = typeof target.closest === 'function' ? target.closest(INTERACTIVE) : null;
    if (hit && hit !== rowEl && rowEl.contains(hit)) return;
    // Dragging across text to copy it is not a click on the row; a plain
    // click opens it even while text selected earlier is still highlighted.
    if (selectingTextIn(rowEl, event, rowPressRef.current)) return;
    onRowClick(row);
  };
  const handleRowKey = (event, row) => {
    if (typeof onRowClick !== 'function' || event.target !== event.currentTarget) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      onRowClick(row);
    }
  };

  const renderActions = (row) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
      {inlineActions.map((a) => {
        if (!isShown(a, row)) return <Box key={a.key || a.label} aria-hidden="true" sx={{ width: ACTION_SLOT, height: ACTION_SLOT, flex: 'none' }} />;
        const label = typeof a.tooltip === 'function' ? a.tooltip(row) : a.label;
        const disabled = isDisabled(a, row);
        return (
          <Tooltip key={a.key || a.label} title={label} describeChild>
            <span>
              <IconButton
                aria-label={`${label}${nameFor(row)}`}
                disabled={disabled}
                onClick={() => a.onClick && a.onClick(row)}
                sx={{ width: ACTION_SLOT, height: ACTION_SLOT, color: a.destructive ? c.DANGER_TEXT : c.INK_2 }}
              >
                {a.icon}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
      {menuActions.length ? (
        menuActions.some((a) => isShown(a, row)) ? (
          <Tooltip title={L('table_more_actions', 'More actions')} describeChild>
            <IconButton
              aria-label={`${L('table_more_actions', 'More actions')}${nameFor(row)}`}
              aria-haspopup="menu"
              onClick={(e) => setRowMenu({ anchor: e.currentTarget, row })}
              sx={{ width: ACTION_SLOT, height: ACTION_SLOT, color: c.INK_2 }}
            >
              <MoreHorizRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Box aria-hidden="true" sx={{ width: ACTION_SLOT, height: ACTION_SLOT, flex: 'none' }} />
        )
      ) : null}
    </Box>
  );

  // ── Empty, loading and failed bodies ─────────────────────────────────
  const leadCols = (selectable ? 1 : 0) + (typeof detailPanel === 'function' ? 1 : 0);
  const colSpan = leadCols + shownCols.length + (hasActions ? 1 : 0);

  const renderEmpty = () => {
    const custom = typeof emptyState === 'function' ? emptyState(info) : null;
    if (custom) return <EmptyState {...custom} />;
    if (trimmedQuery) {
      return (
        <EmptyState
          icon={<SearchOffRoundedIcon />}
          title={t('table_empty_search', { query: trimmedQuery, defaultValue: 'Nothing matches “{{query}}”' })}
          actionLabel={search !== false || onSearchChange ? L('clear_search', 'Clear search') : undefined}
          onAction={search !== false || onSearchChange ? () => setQuery('') : undefined}
        />
      );
    }
    if (emptyState && typeof emptyState === 'object') return <EmptyState {...emptyState} />;
    return <EmptyState icon={<InboxOutlinedIcon />} title={L('table_empty', 'Nothing here yet')} />;
  };

  const pageKeys = pageRows.map(keyOf);
  const pageSelectedCount = pageKeys.filter((k) => selectedKeys.includes(k)).length;
  const togglePage = () => {
    if (pageSelectedCount === pageKeys.length) setSelected(selectedKeys.filter((k) => !pageKeys.includes(k)));
    else setSelected(selectedKeys.concat(pageKeys));
  };

  const menuRow = rowMenu ? rowMenu.row : null;
  const shownMenu = menuRow ? menuActions.filter((a) => isShown(a, menuRow)) : [];
  const plainMenu = shownMenu.filter((a) => !a.destructive);
  const dangerMenu = shownMenu.filter((a) => a.destructive);
  const runMenu = (a) => {
    const row = menuRow;
    setRowMenu(null);
    if (row && a.onClick) a.onClick(row);
  };

  const scrollSx =
    maxHeight === null || maxHeight === false
      ? {}
      : {
          [theme.breakpoints.up('md')]: {
            maxHeight,
            '@supports (height: 100dvh)': { maxHeight: String(maxHeight).replace(/100vh/g, '100dvh') },
          },
        };

  const showSearch = search !== false && !(selectable && selectedKeys.length > 0);
  const bulk = (Array.isArray(bulkActions) ? bulkActions : []).filter(Boolean);

  return (
    <Paper
      component="section"
      aria-label={tableName}
      dir={i18n.dir()}
      sx={{ overflow: 'hidden', minWidth: 0, ...(sx || {}) }}
    >
      {/* Header: count, search, page buttons, density, columns, export */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderBottom: `1px solid ${c.OUTLINE}` }}>
        {selectable && selectedKeys.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, marginInlineEnd: 'auto' }}>
            <Typography role="status" aria-live="polite" sx={{ fontSize: 15, fontWeight: 700, color: c.INK }}>
              {t('table_selected', { count: selectedKeys.length, n: fmt(selectedKeys.length), defaultValue: '{{n}} selected' })}
            </Typography>
            {bulk.map((b) => (
              <Button
                key={b.key || b.label}
                variant="outlined"
                color={b.destructive ? 'error' : 'neutral'}
                startIcon={b.icon || undefined}
                disabled={typeof b.disabled === 'function' ? !!b.disabled(selectedRows) : !!b.disabled}
                onClick={() => b.onClick && b.onClick(selectedRows)}
              >
                {b.label}
              </Button>
            ))}
            <Button variant="text" color="neutral" onClick={() => setSelected([])}>
              {L('table_clear_selection', 'Clear selection')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, marginInlineEnd: 'auto', minWidth: 0 }}>
            <Typography component="p" role="status" aria-live="polite" sx={{ fontSize: 16, fontWeight: 700, color: c.INK, lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>
              {countLine}
            </Typography>
            {subtitle ? (
              <Typography component="span" sx={{ fontSize: 13.5, color: c.TEXT_3, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        )}
        {headerContent ? <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>{headerContent}</Box> : null}
        {showSearch ? (
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder || L('search', 'Search')}
            width={{ xs: '100%', sm: 280 }}
          />
        ) : null}
        {toolbar ? <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>{toolbar}</Box> : null}
        {densityToggle ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.INK_2, display: { xs: 'none', sm: 'inline' } }}>
              {L('rows', 'Rows')}
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={density} onChange={changeDensity} aria-label={L('table_density', 'Row density')}>
              <ToggleButton value="comfortable" sx={{ px: 1.25, gap: 0.75, textTransform: 'none', fontWeight: 600 }}>
                <TableRowsOutlinedIcon fontSize="small" />
                {L('table_density_comfortable', 'Comfortable')}
              </ToggleButton>
              <ToggleButton value="compact" sx={{ px: 1.25, gap: 0.75, textTransform: 'none', fontWeight: 600 }}>
                <DensitySmallRoundedIcon fontSize="small" />
                {L('table_density_compact', 'Compact')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}
        {columnsMenu && hideableCols.length > 1 ? (
          <Button
            variant="outlined"
            color="neutral"
            startIcon={<ViewColumnOutlinedIcon />}
            aria-haspopup="menu"
            aria-expanded={columnsAnchor ? 'true' : 'false'}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            {L('table_columns', 'Columns')}
            {hiddenCount ? (
              <Box component="span" sx={{ ml: 0.75, fontWeight: 500, color: c.INK_2 }}>
                {t('table_columns_hidden', { count: hiddenCount, defaultValue: '{{count}} hidden' })}
              </Box>
            ) : null}
          </Button>
        ) : null}
        {canCsv && canPrint ? (
          <Button
            variant="outlined"
            color="neutral"
            startIcon={<FileDownloadOutlinedIcon />}
            aria-haspopup="menu"
            disabled={exportDisabled}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            {calmCaps(L('export', 'Export'))}
          </Button>
        ) : canCsv ? (
          <Button variant="outlined" color="neutral" startIcon={<FileDownloadOutlinedIcon />} disabled={exportDisabled} onClick={handleCsv}>
            {L('export_download_csv', 'Download CSV')}
          </Button>
        ) : canPrint ? (
          <Button variant="outlined" color="neutral" startIcon={<PrintOutlinedIcon />} disabled={exportDisabled} onClick={handlePrint}>
            {L('export_print_pdf', 'Print or save PDF')}
          </Button>
        ) : null}
      </Box>
      {refreshing && !loading ? <LinearProgress aria-label={L('loading', 'Loading…')} sx={{ height: 3 }} /> : null}

      {error ? (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <InlineMessage
            kind="error"
            title={error.title}
            actionLabel={typeof error.onRetry === 'function' ? L('retry', 'Retry') : undefined}
            onAction={typeof error.onRetry === 'function' ? error.onRetry : undefined}
          >
            {error.message || L('load_failed_body', 'Check the internet connection, then try again.')}
          </InlineMessage>
        </Box>
      ) : null}

      {/* On a computer the table scrolls inside the card under a sticky
          header; on a phone the page scrolls instead. */}
      <TableContainer sx={scrollSx}>
        <Table
          stickyHeader
          size={compact ? 'small' : 'medium'}
          aria-label={tableName}
          sx={{
            minWidth,
            '& .MuiTableCell-root': { px: 1.25, py: compact ? 0.5 : 1, fontSize: 14, lineHeight: 1.45, verticalAlign: 'middle', color: c.INK },
            '& .MuiTableCell-root:first-of-type': { paddingInlineStart: '16px' },
            '& .MuiTableCell-root:last-of-type': { paddingInlineEnd: '16px' },
            '& .MuiTableCell-head': {
              bgcolor: ZEBRA,
              color: c.INK_2,
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              py: 1.25,
              borderBottom: `1px solid ${c.OUTLINE_STRONG}`,
            },
            '& tbody tr.dd-zebra > td': { bgcolor: ZEBRA },
            '& tbody tr.dd-row:hover > td': { bgcolor: c.ACCENT_TINT },
            '& tbody tr.dd-row.Mui-selected > td': { bgcolor: c.ACCENT_TINT },
          }}
        >
          <TableHead>
            <TableRow>
              {selectable ? (
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Checkbox
                    size="small"
                    indeterminate={pageSelectedCount > 0 && pageSelectedCount < pageKeys.length}
                    checked={pageKeys.length > 0 && pageSelectedCount === pageKeys.length}
                    onChange={togglePage}
                    disabled={!pageKeys.length}
                    inputProps={{ 'aria-label': L('table_select_page', 'Select the rows on this page') }}
                  />
                </TableCell>
              ) : null}
              {typeof detailPanel === 'function' ? (
                <TableCell sx={{ width: 48 }}>
                  <Box component="span" sx={srOnly}>
                    {L('details', 'Details')}
                  </Box>
                </TableCell>
              ) : null}
              {shownCols.map((col) => {
                const active = !!sort && sort.by === col.id;
                const canSort = col.sortable || typeof col.customSort === 'function';
                return (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sortDirection={active ? sort.dir : false}
                    sx={{ ...(col.width ? { width: col.width } : {}), ...(col.headerSx || {}) }}
                  >
                    {canSort ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.dir : 'asc'}
                        onClick={() =>
                          setSort(
                            active
                              ? { by: col.id, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
                              : { by: col.id, dir: ['date', 'datetime', 'time', 'number', 'money'].includes(col.type) ? 'desc' : 'asc' }
                          )
                        }
                      >
                        {col.title}
                      </TableSortLabel>
                    ) : (
                      col.title
                    )}
                  </TableCell>
                );
              })}
              {hasActions ? (
                <TableCell align="right" sx={{ width: actionsWidth }}>
                  <Box component="span" sx={srOnly}>
                    {L('actions', 'Actions')}
                  </Box>
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableSkeletonRows rows={6} columns={colSpan} rowHeight={rowHeight} />
            ) : pageRows.length ? (
              pageRows.map((row, index) => {
                const key = keyOf(row);
                const open = typeof detailPanel === 'function' && expanded.includes(key);
                const isSelected = selectable && selectedKeys.includes(key);
                const extraSx = typeof rowSx === 'function' ? rowSx(row) : null;
                return (
                  <React.Fragment key={key}>
                    <TableRow
                      hover
                      selected={isSelected}
                      className={`dd-row${index % 2 === 1 ? ' dd-zebra' : ''}`}
                      onMouseDown={onRowClick ? (e) => { rowPressRef.current = pressPoint(e); } : undefined}
                      onClick={onRowClick ? (e) => handleRowClick(e, row) : undefined}
                      onKeyDown={onRowClick ? (e) => handleRowKey(e, row) : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      sx={{
                        height: rowHeight,
                        ...(onRowClick ? { cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: -2 } } : {}),
                        ...(extraSx || {}),
                      }}
                    >
                      {selectable ? (
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => setSelected(isSelected ? selectedKeys.filter((k) => k !== key) : selectedKeys.concat([key]))}
                            inputProps={{ 'aria-label': `${L('table_select_row', 'Select row')}${nameFor(row)}` }}
                          />
                        </TableCell>
                      ) : null}
                      {typeof detailPanel === 'function' ? (
                        <TableCell sx={{ width: 48 }}>
                          <IconButton
                            size="small"
                            aria-expanded={open ? 'true' : 'false'}
                            aria-label={`${open ? L('table_hide_details', 'Hide details') : L('table_show_details', 'Show details')}${nameFor(row)}`}
                            onClick={() => toggleExpanded(key)}
                          >
                            <KeyboardArrowDownRoundedIcon sx={{ transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }} />
                          </IconButton>
                        </TableCell>
                      ) : null}
                      {shownCols.map((col) => (
                        <TableCell key={col.id} align={col.align} sx={cellSxOf(col)}>
                          {renderCell(col, row)}
                        </TableCell>
                      ))}
                      {hasActions ? <TableCell align="right">{renderActions(row)}</TableCell> : null}
                    </TableRow>
                    {typeof detailPanel === 'function' ? (
                      <TableRow className={index % 2 === 1 ? 'dd-zebra' : undefined}>
                        <TableCell colSpan={colSpan} sx={{ py: '0 !important', borderBottom: open ? undefined : 0 }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2 }}>{detailPanel(row)}</Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : error ? null : (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ borderBottom: 0 }}>
                  {renderEmpty()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paging && !loading && sorted.length ? (
        <TablePagination
          component="div"
          count={sorted.length}
          page={safePage}
          onPageChange={(e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={sizes}
          onRowsPerPageChange={(e) => {
            const value = parseInt(e.target.value, 10) || fallbackSize;
            setRowsPerPage(value);
            writeTablePref(prefsKey, 'rpp', uid, value);
            setPage(0);
          }}
          labelRowsPerPage={L('rows_per_page', 'Rows per page')}
          labelDisplayedRows={({ from, to, count }) => `${fmt(from)}–${fmt(to)} ${L('of', 'of')} ${fmt(count)}`}
          getItemAriaLabel={(type) =>
            type === 'next'
              ? L('next_page_tooltip', 'Next page')
              : type === 'previous'
                ? L('previous_page_tooltip', 'Previous page')
                : type === 'first'
                  ? L('first_page_tooltip', 'First page')
                  : L('last_page_tooltip', 'Last page')
          }
          sx={{
            borderTop: `1px solid ${c.OUTLINE}`,
            // On a 360-px phone the controls wrap instead of running off the card.
            '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 0.5, pl: { xs: 1, sm: 2 } },
            '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
          }}
        />
      ) : null}

      {/* Row menu */}
      <Menu anchorEl={rowMenu ? rowMenu.anchor : null} open={Boolean(rowMenu)} onClose={() => setRowMenu(null)}>
        {plainMenu.map((a) => (
          <MenuItem key={a.key || a.label} disabled={isDisabled(a, menuRow)} onClick={() => runMenu(a)}>
            {a.icon ? <ListItemIcon sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}>{a.icon}</ListItemIcon> : null}
            <ListItemText primary={typeof a.tooltip === 'function' ? a.tooltip(menuRow) : a.label} />
          </MenuItem>
        ))}
        {plainMenu.length && dangerMenu.length ? <Divider /> : null}
        {dangerMenu.map((a) => (
          <MenuItem key={a.key || a.label} disabled={isDisabled(a, menuRow)} onClick={() => runMenu(a)} sx={{ color: c.DANGER_TEXT }}>
            {a.icon ? <ListItemIcon sx={{ color: c.DANGER_TEXT, '& .MuiSvgIcon-root': { fontSize: 20 } }}>{a.icon}</ListItemIcon> : null}
            <ListItemText primary={typeof a.tooltip === 'function' ? a.tooltip(menuRow) : a.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Columns */}
      <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)}>
        {hideableCols.map((col) => (
          <MenuItem
            key={col.id}
            role="menuitemcheckbox"
            aria-checked={hiddenIds.includes(col.id) ? 'false' : 'true'}
            onClick={() => toggleColumn(col.id)}
            sx={{ minWidth: 200 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Checkbox edge="start" size="small" checked={!hiddenIds.includes(col.id)} tabIndex={-1} disableRipple inputProps={{ 'aria-hidden': 'true' }} />
            </ListItemIcon>
            <ListItemText primary={typeof col.title === 'string' ? col.title : col.exportTitle || col.id} />
          </MenuItem>
        ))}
      </Menu>

      {/* Export */}
      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
        <MenuItem onClick={handleCsv}>
          <ListItemIcon>
            <FileDownloadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={L('export_download_csv', 'Download CSV')} />
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={L('export_print_pdf', 'Print or save PDF')} />
        </MenuItem>
      </Menu>
    </Paper>
  );
}
